/**
 * Flat colour exports, re-exported from styles/theme.ts.
 *
 * This file defines no colours. styles/theme.ts holds the single definition of
 * every colour the app uses; this is the flat read path for code that cannot
 * reach the Emotion theme — the antd config object, the Chart.js setup, SVG
 * icon modules.
 *
 * Four names below are historical aliases, kept so existing call sites keep
 * working: openGreen, yellow, errorRed, brandRed. Each resolves to the
 * canonical token of the same value. Prefer the canonical name in new code.
 */
import { lightTheme } from "styles/theme";

const c = lightTheme.colors;

// Blues
export const blue50 = c.blue50;
export const blue100 = c.blue100;
export const blue500 = c.blue500;
export const blue600 = c.blue600;
export const blue700 = c.blue700;
export const blue900 = c.blue900;

// Greys
export const grey10 = c.grey10;
export const grey25 = c.grey25;
export const grey50 = c.grey50;
export const grey100 = c.grey100;
export const grey400 = c.grey400;
export const grey500 = c.grey500;
export const grey700 = c.grey700;
export const grey900 = c.grey900;

// Reds
export const red75 = c.red75;
export const red100 = c.red100;
export const red400 = c.red400;
export const red500 = c.red500;
export const red600 = c.red600;
export const red700 = c.red700;
export const red800 = c.red800;

// Greens
export const green500 = c.green500;
export const green600 = c.green600;
export const green800 = c.green800;

export const openGreen = c.green600; // historical alias for green600

// Basics
export const white = c.white;

// Alpha
export const opacityDark2 = c.opacityDark2;

// Yellows
export const yellow50 = c.yellow50;
export const yellow200 = c.yellow200;
export const yellow300 = c.yellow300;
export const yellow400 = c.yellow400;
export const yellow500 = c.yellow500;
export const yellow600 = c.yellow600;
export const yellow = c.yellow500; // historical alias for yellow500

// Semantics

// semantics // primary
export const sysPrimaryDefault = c.sysPrimaryDefault;
export const sysPrimaryHover = c.sysPrimaryHover;
export const sysPrimarySubtle = c.sysPrimarySubtle;

// semantics // text
export const sysTextDefault = c.sysTextDefault;
export const sysTextSecondary = c.sysTextSecondary;
export const sysTextPlaceholder = c.sysTextPlaceholder;

// semantics // background
export const sysBackgroundAlternative = c.sysBackgroundAlternative;
export const sysBackgroundInactive = c.sysBackgroundInactive;

// semantics // border
export const sysBorderDecorative = c.sysBorderDecorative;
export const sysBorderFunctional = c.sysBorderFunctional;

// semantics // signal
export const sysSignalDefault = c.sysSignalDefault;
export const sysSignalKeyword = c.sysSignalKeyword;
export const sysSignalCompetitor = c.sysSignalCompetitor;
export const sysSignalPartner = c.sysSignalPartner;
export const sysSignalBuyer = c.sysSignalBuyer;

// semantics // warning
export const sysWarningDefault = c.sysWarningDefault;
export const sysWarningSubtle = c.sysWarningSubtle;

// semantics // status
export const sysStatusPositive = c.sysStatusPositive;
export const sysStatusNegative = c.sysStatusNegative;
export const sysStatusInProgress = c.sysStatusInProgress;

// semantics // procurement stage
export const sysProcStageLive = c.sysProcStageLive;
export const sysProcStageExpiredContract = c.sysProcStageExpiredContract;

export const errorRed = c.red600; // historical alias for red600
export const brandRed = c.red400; // historical alias for red400

// External brands
export const hubspotOrange = c.hubspotOrange;
export const salesforceBlue = c.salesforceBlue;
export const dynamicsBlue = c.dynamicsBlue;
export const zohoBlue = c.zohoBlue;

// Default workflow stages
export const toEngageIconColor = c.toEngageIconColor;
export const toEngageBackgroundColor = c.toEngageBackgroundColor;
export const nurturingIconColor = c.nurturingIconColor;
export const nurturingBackgroundColor = c.nurturingBackgroundColor;
export const meetingBookedIconColor = c.meetingBookedIconColor;
export const meetingBookedBackgroundColor = c.meetingBookedBackgroundColor;
export const bidCreatedIconColor = c.bidCreatedIconColor;
export const bidCreatedBackgroundColor = c.bidCreatedBackgroundColor;
export const disqualifiedIconColor = c.disqualifiedIconColor;
export const disqualifiedBackgroundColor = c.disqualifiedBackgroundColor;
export const wonIconColor = c.wonIconColor;
export const wonBackgroundColor = c.wonBackgroundColor;
export const lostIconColor = c.lostIconColor;
export const lostBackgroundColor = c.lostBackgroundColor;
